import { toast } from '@/components/ui/use-toast';
import { GenericErrorResponse, InternalServerErrorResponse } from '@brainvaulttech/dgr-react-sdk';
import api from '@/services/axiosInstance';
import { APPLICATION_NAME } from '@/utils/constant';
import axios from 'axios';

interface ErrorLogPayload {
  applicationName: string;
  errorMessage: string;
  severity: 'ERROR' | 'WARNING' | 'INFO';
  stackTrace?: string;
  metadata?: Record<string, any>;
}

/**
 * Safely stringify an object, handling circular references
 * @param obj - The object to stringify
 * @returns JSON string or error message if stringification fails
 */
const safeJSONStringify = (obj: any): string => {
  const seen = new WeakSet();
  try {
    return JSON.stringify(obj, (key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return '[Circular Reference]';
        }
        seen.add(value);
      }
      return value;
    });
  } catch (error) {
    return '[Unable to stringify object]';
  }
};

/**
 * Sanitize sensitive data from strings
 * @param text - The text to sanitize
 * @returns Sanitized text with sensitive patterns removed
 */
const sanitizeSensitiveData = (text: string): string => {
  // Patterns for common sensitive data
  const patterns = [
    // API keys in key-value format
    /(['"]?(?:api[_-]?key|secret)['"]\s*[:=]\s*['"]?)[\w\-._~:/?#[\]@!$&'()*+,;=%]+(['"]?)/gi,
    // Access and refresh tokens in key-value format
    /(['"]?(?:access[_-]?token|refresh[_-]?token|token)['"]\s*[:=]\s*['"]?)[\w\-._~:/?#[\]@!$&'()*+,;=%]+(['"]?)/gi,
    // Authorization headers with bearer token
    /(['"]?(?:bearer|auth(?:orization)?)['"]\s*[:=]\s*['"]?)[\w\-._~:/?#[\]@!$&'()*+,;=%]+(['"]?)/gi,
    // Bearer tokens in Authorization headers
    /\bBearer\s+[\w\-._~+/]+=*/gi,
    // Base64-encoded tokens (50+ chars to reduce false positives, common in JWT/API keys)
    /\b[A-Za-z0-9+/]{50,}={0,2}\b/g,
    // JWT tokens (specific format)
    /eyJ[A-Za-z0-9_-]*\.eyJ[A-Za-z0-9_-]*\.[A-Za-z0-9_-]*/g,
    // Email addresses (basic pattern)
    /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g,
    // Credit card numbers (basic pattern)
    /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g,
    // Social security numbers (US format)
    /\b\d{3}-\d{2}-\d{4}\b/g,
    // Phone numbers (basic pattern)
    /\b(\+\d{1,3}[- ]?)?\(?\d{3}\)?[- ]?\d{3}[- ]?\d{4}\b/g,
  ];

  let sanitized = text;
  patterns.forEach((pattern) => {
    sanitized = sanitized.replace(pattern, '[REDACTED]');
  });

  return sanitized;
};

/**
 * Sanitize error message for user display
 * @param message - The raw error message
 * @returns User-friendly error message
 */
const sanitizeUserErrorMessage = (message: string): string => {
  // First sanitize sensitive data
  const sanitized = sanitizeSensitiveData(message);

  // Replace technical jargon with user-friendly messages using word boundaries
  const technicalPatterns: { pattern: RegExp; replacement: string }[] = [
    { pattern: /\bnetwork\s+error\b/i, replacement: 'Unable to connect to the server. Please check your internet connection.' },
    { pattern: /\btimeout\b/i, replacement: 'The request took too long. Please try again.' },
    { pattern: /\b401\b/, replacement: 'Your session has expired. Please log in again.' },
    { pattern: /\b403\b/, replacement: 'You do not have permission to perform this action.' },
    { pattern: /\b404\b/, replacement: 'The requested resource was not found.' },
    { pattern: /\b500\b/, replacement: 'A server error occurred. Please try again later.' },
    { pattern: /\b503\b/, replacement: 'The service is temporarily unavailable. Please try again later.' },
  ];

  for (const { pattern, replacement } of technicalPatterns) {
    if (pattern.test(sanitized)) {
      return replacement;
    }
  }

  // If the message is too technical (contains common technical terms), use a generic message
  const technicalTermRegexes = [
    /\bundefined\b/i,
    /\bnull\b/i,
    /\bstack\s+trace\b/i,
    /\bexception\b/i,
    /\bTypeError\b/,
    /\bReferenceError\b/,
  ];
  
  if (technicalTermRegexes.some(regex => regex.test(sanitized))) {
    return 'An unexpected error occurred. Please try again.';
  }

  return sanitized;
};

/**
 * Type guard to check if an object has the GenericErrorResponse structure
 * @param obj - The object to check
 * @returns True if the object matches GenericErrorResponse structure
 */
const isGenericErrorResponse = (obj: any): obj is GenericErrorResponse | InternalServerErrorResponse => {
  return (
    obj &&
    typeof obj === 'object' &&
    'error' in obj &&
    obj.error &&
    typeof obj.error === 'object' &&
    ('details' in obj.error || 'code' in obj.error || 'statusCode' in obj.error)
  );
};

/**
 * Logs error details to the server
 * @param error - The error object or error response
 * @param severity - Error severity level (default: 'ERROR')
 * @param additionalMetadata - Additional metadata to include in the log
 */
export const logErrorToServer = (
  error: Error | GenericErrorResponse | InternalServerErrorResponse | unknown,
  severity: 'ERROR' | 'WARNING' | 'INFO' = 'ERROR',
  additionalMetadata?: Record<string, any>
): void => {
  try {
    let errorMessage = 'Unknown error occurred';
    let stackTrace: string | undefined;
    let metadata: Record<string, any> = {
      url: window.location.href,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      ...additionalMetadata,
    };

    // Handle different error types
    if (error instanceof Error) {
      errorMessage = error.message;
      stackTrace = error.stack;
      metadata.errorName = error.name;
    } else if (isGenericErrorResponse(error)) {
      errorMessage = typeof error.error.details === 'string' 
        ? error.error.details 
        : safeJSONStringify(error.error.details);
      metadata.errorCode = error.error.code;
      metadata.statusCode = error.error.statusCode;
    } else if (error && typeof error === 'object') {
      errorMessage = safeJSONStringify(error);
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    // Sanitize sensitive data from error message and stack trace
    errorMessage = sanitizeSensitiveData(errorMessage);
    if (stackTrace) {
      stackTrace = sanitizeSensitiveData(stackTrace);
    }

    // Sanitize metadata values individually to preserve JSON structure
    if (typeof metadata === 'object') {
      const sanitizedMetadata: Record<string, any> = {};
      for (const [key, value] of Object.entries(metadata)) {
        if (typeof value === 'string') {
          sanitizedMetadata[key] = sanitizeSensitiveData(value);
        } else if (value && typeof value === 'object') {
          // For nested objects, stringify and check if sanitization would redact data
          const valueString = safeJSONStringify(value);
          const sanitizedString = sanitizeSensitiveData(valueString);
          // If sensitive data was found, don't include the original object
          sanitizedMetadata[key] = valueString !== sanitizedString 
            ? '[Redacted Object]' 
            : value;
        } else {
          sanitizedMetadata[key] = value;
        }
      }
      metadata = sanitizedMetadata;
    }

    const payload: ErrorLogPayload = {
      applicationName: APPLICATION_NAME,
      errorMessage,
      severity,
      stackTrace,
      metadata,
    };

    // Send error log to server (fire and forget - don't await to avoid blocking user experience)
    api.post('/log/error', payload).catch((logError) => {
      // Silently fail if logging fails to avoid infinite error loops
      console.error('Failed to log error to server:', logError);
    });
  } catch (logError) {
    // Silently fail if logging preparation fails
    console.error('Failed to prepare error log:', logError);
  }
};

const _fallbackAxiosError: GenericErrorResponse = {
  error: { code: 'UNKNOWN', message: 'An unexpected error occurred' },
} as GenericErrorResponse;

/** Build a typed GenericErrorResponse for user-facing messages (no API payload). */
export const toUserFacingError = (message: string, code = 'USER_FACING'): GenericErrorResponse => ({
  error: { code, message },
});

/** Toast a user-facing error via the standard displayError path. */
export const displayUserFacingError = (message: string, duration = 5000) => {
  displayError(toUserFacingError(message), duration);
};

/**
 * Normalizes an unknown catch-block value into a typed GenericErrorResponse.
 * Use this in every service catch block instead of casting with `as GenericErrorResponse`.
 * Guarantees a truthy, typed error even when the request fails without a response
 * (network errors, CORS, timeouts).
 */
export const normalizeAxiosError = (error: unknown): GenericErrorResponse => {
  if (axios.isAxiosError(error)) {
    return (error.response?.data as GenericErrorResponse | undefined) ?? _fallbackAxiosError;
  }
  return _fallbackAxiosError;
};

export const displayError = (
  error: GenericErrorResponse | InternalServerErrorResponse | unknown,
  duration = 5000,
  className?: string,
  logToServer = false
) => {
  // Normalize the error if it is an Axios error
  let normalizedError = error;
  if (axios.isAxiosError(error)) {
    normalizedError = error.response?.data;
  } else if (error && typeof error === 'object' && 'response' in error) {
    const responseData = (error as any).response?.data;
    if (isGenericErrorResponse(responseData)) {
      normalizedError = responseData;
    }
  }

  // Log error to server if enabled
  if (logToServer && normalizedError) {
    logErrorToServer(normalizedError);
  }

  const errorObj = (normalizedError as any)?.error;
  const details = errorObj?.details;
  const message = errorObj?.message;

  if (Array.isArray(details) && details.length > 0) {
    details.forEach((detail) => {
      toast({
        description: (detail as { message: string; path?: string; code?: string })?.message,
        variant: 'destructive',
        ...(className && { className }),
        ...(duration && { duration }),
      });
    });
  } else if (typeof details === 'string' && details.trim().length > 0) {
    toast({
      description: details,
      variant: 'destructive',
      ...(className && { className }),
      ...(duration && { duration }),
    });
  } else if (typeof message === 'string' && message.trim().length > 0) {
    toast({
      description: message,
      variant: 'destructive',
      ...(className && { className }),
      ...(duration && { duration }),
    });
  } else {
    toast({
      description: 'An error occurred. Please try again later.',
      variant: 'destructive',
      ...(className && { className }),
      ...(duration && { duration }),
    });
  }
};

/**
 * Handles JavaScript errors globally - can be used with error boundaries
 * @param error - The error object
 * @param errorInfo - Additional error information (e.g., component stack)
 */
export const handleError = (
  error: Error | unknown,
  errorInfo?: { componentStack?: string; [key: string]: any }
): void => {
  // Log to server
  logErrorToServer(error, 'ERROR', errorInfo);

  // Display sanitized user-friendly message to user
  let errorMessage = 'An unexpected error occurred';
  if (error instanceof Error) {
    errorMessage = sanitizeUserErrorMessage(error.message);
  }

  toast({
    description: errorMessage,
    variant: 'destructive',
    duration: 5000,
  });
};
